import type { UsageHourlyUnitQuantity } from '@nadoutong/contracts';
import { formatTokens, parseTimestampMs } from '../../components/formatters.ts';

/**
 * Pure display/math layer for the usage-analytics page group (`/usage`
 * + `/requests`).
 *
 * It exists because those two screens render the same quantities —
 * compact token counts, percent and ratio rendering, window
 * boundaries, range math — and every private copy of that logic is a
 * drift that only shows up as two pages disagreeing about the same
 * number. This module is their ONE home. `/usage`, `/requests` and
 * `Optimization.tsx` all draw their windows from it; no private range
 * math is left in the app, and none may come back.
 *
 * Scope boundary against `components/formatters.ts`: that module owns
 * money, token grouping and timestamp semantics and is shared by all
 * four apps (it is byte-identical with console-web's copy). Calendar
 * math and page-group presentation belong HERE so that invariant is
 * not disturbed. Anything below that needs a token count or a currency
 * string calls into it rather than reimplementing.
 *
 * Everything here is a pure formatter or pure math — no React, no
 * locale state. Anything needing translation takes the display string
 * from the caller.
 */

const COMPACT_FORMATTER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const PERCENT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});

// ─── Local-day range math (date-range filter, /usage + /requests) ─────────
//
// The calendar filter works in LOCAL `YYYY-MM-DD` day strings end to
// end; only `dayRangeToIsoWindow` converts to instants, at the moment
// the query is built. Lexicographic comparison on these strings is
// chronological, which the helpers below rely on.

/** Inclusive local-day window; both endpoints are `YYYY-MM-DD`. */
export interface DayRange {
  readonly from: string;
  readonly to: string;
}

/** The part of a range preset this module needs: its id and its window. */
export interface DayRangePresetShape {
  readonly id: string;
  readonly range: DayRange;
}

/** `YYYY-MM-DD` for a Date read in the LOCAL zone, never UTC. */
export function localDayString(date: Date): string {
  return [
    String(date.getFullYear()).padStart(4, '0'),
    pad2(date.getMonth() + 1),
    pad2(date.getDate()),
  ].join('-');
}

/** Today as a local day string — the anchor every preset is built from. */
export function todayLocalDay(): string {
  return localDayString(new Date());
}

/** Move a local day by whole days; month/year rollover is the Date's job. */
export function shiftLocalDay(day: string, deltaDays: number): string {
  const date = dayToLocalDate(day);
  date.setDate(date.getDate() + deltaDays);
  return localDayString(date);
}

/** Inclusive rolling window: `days` calendar days ending on `today`. */
export function rollingDayRange(today: string, days: number): DayRange {
  return { from: shiftLocalDay(today, -(days - 1)), to: today };
}

/** First of the current month through `today`, inclusive. */
export function monthToDateRange(today: string): DayRange {
  return { from: `${today.slice(0, 8)}01`, to: today };
}

/** ISO instant window covering the inclusive local-day range. */
export function dayRangeToIsoWindow(range: DayRange): {
  readonly from: string;
  readonly to: string;
} {
  const from = dayToLocalDate(range.from);
  const to = dayToLocalDate(range.to);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Validate a URL day param: `YYYY-MM-DD` shape AND a real calendar date. */
export function parseDayParam(value: string | null): string | null {
  if (value === null || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = dayToLocalDate(value);
  return localDayString(date) === value ? value : null;
}

/**
 * Resolve URL search params to a range selection. A `rangeParam`
 * matching a preset id (directly or through `aliases`, e.g. the legacy
 * `24h`) wins; `custom` requires a valid, non-inverted, non-future
 * window within `maxSpanDays`. Anything else falls back to
 * `defaultPresetId` — the backend 400 guard stays as the last line.
 */
export function resolveRangeSelection(options: {
  readonly rangeParam: string | null;
  readonly fromParam: string | null;
  readonly toParam: string | null;
  readonly presets: readonly DayRangePresetShape[];
  readonly defaultPresetId: string;
  readonly maxSpanDays: number;
  readonly today: string;
  readonly aliases?: Readonly<Record<string, string>>;
}): { readonly presetId: string | null; readonly range: DayRange } {
  const resolvedId =
    options.rangeParam !== null
      ? (options.aliases?.[options.rangeParam] ?? options.rangeParam)
      : null;
  if (resolvedId !== null && resolvedId !== 'custom') {
    const preset = options.presets.find((candidate) => candidate.id === resolvedId);
    if (preset !== undefined) return { presetId: preset.id, range: preset.range };
  }
  if (resolvedId === 'custom') {
    const from = parseDayParam(options.fromParam);
    const to = parseDayParam(options.toParam);
    if (
      from !== null &&
      to !== null &&
      from <= to &&
      to <= options.today &&
      daySpanInclusive(from, to) <= options.maxSpanDays
    ) {
      return { presetId: null, range: { from, to } };
    }
  }
  const fallback = options.presets.find((candidate) => candidate.id === options.defaultPresetId);
  if (fallback !== undefined) return { presetId: fallback.id, range: fallback.range };
  // Unreachable when callers pass a defaultPresetId from their own
  // preset list; a same-day range keeps the type honest regardless.
  return { presetId: null, range: { from: options.today, to: options.today } };
}

/** Inclusive endpoint count: `2026-06-01..2026-06-01` is 1 day. */
export function daySpanInclusive(from: string, to: string): number {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return (
    Math.round((dayToLocalDate(to).getTime() - dayToLocalDate(from).getTime()) / MS_PER_DAY) + 1
  );
}

function dayToLocalDate(day: string): Date {
  return new Date(
    Number.parseInt(day.slice(0, 4), 10),
    Number.parseInt(day.slice(5, 7), 10) - 1,
    Number.parseInt(day.slice(8, 10), 10),
  );
}

// ─── Trend bucket axis (/usage admin trend) ───────────────────────────
//
// The admin trend arrives as a flat point list keyed by a server-made
// bucket STRING, and SQL `GROUP BY` only emits a row for buckets that
// had traffic. Reading the returned key set as the axis therefore drops
// every zero-traffic bucket without a trace (#46). The axis is rebuilt
// here from the window instead, so it is a property of what was ASKED
// FOR rather than of what came back.

/**
 * The timezone the SERVER cuts trend buckets in — the single place this
 * assumption is written down.
 *
 * `adminBucketExpression()` in router's `internal-usage.ts` hardcodes
 * `AT TIME ZONE 'UTC'` for all three granularities, while the window
 * this page sends is built from LOCAL calendar days. The mismatch is
 * why the two ends of a window are partial buckets, and why a
 * single-day selection can return a bucket labelled with the previous
 * date. Making the server cut buckets in the observer's zone is tracked
 * as router issue #1678.
 *
 * When that lands, flipping this constant is NOT sufficient on its own.
 * The bucket math below is deliberately written in UTC (`Date.UTC`,
 * `getUTC*`, `toISOString`) and does not read this constant, so it must
 * be parameterized on the zone at the same time — otherwise the axis
 * keeps materializing UTC-cut keys, the server starts returning
 * locally-cut ones, and the union leaves a permanently empty phantom
 * bucket at the leading edge. `bucketDaysTouched` in
 * `test/usage-bucket-axis.test.ts` derives its expected length from
 * this constant independently, so that mistake goes red rather than
 * shipping quietly.
 */
export const TREND_BUCKET_TIMEZONE = 'UTC';

/** Bucket width of the admin trend; mirrors `AdminUsageGranularity`. */
export type TrendBucketGranularity = 'day' | 'week' | 'month';

/** A materialized bucket axis: every bucket, plus which ones are partial. */
export interface TrendBucketAxis {
  /** Chronological bucket keys — lexicographic order IS chronological. */
  readonly buckets: readonly string[];
  /** Aligned by index with `buckets`; true when the window clips it. */
  readonly incomplete: readonly boolean[];
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Full bucket axis for a window, in the server's bucket timezone.
 *
 * The axis is the materialized window UNION whatever the server
 * actually returned. The union half is not defensive padding: dropping
 * a returned bucket because the axis did not predict it would lose
 * traffic the reader has no other way to see — a worse failure than the
 * one being fixed. (It would not, on its own, reconcile the chart with
 * the summary card: `SERIES_LIMIT` in `AdminPanels.tsx` already keeps
 * only the top few groups, so the drawn stack is a subset of the total
 * regardless.)
 *
 * A bucket is `incomplete` when its own half-open `[start, end)` span is
 * not fully covered by the inclusive window — the two ends of any local
 * window over UTC buckets, and any bucket the server returned from
 * outside the window entirely.
 */
export function trendBucketAxis(options: {
  readonly window: { readonly from: string; readonly to: string };
  readonly granularity: TrendBucketGranularity;
  readonly serverBuckets: readonly string[];
}): TrendBucketAxis {
  const fromMs = parseTimestampMs(options.window.from);
  const toMs = parseTimestampMs(options.window.to);
  const materialized =
    Number.isFinite(fromMs) && Number.isFinite(toMs) && fromMs <= toMs
      ? materializeBuckets(fromMs, toMs, options.granularity)
      : [];
  // Plain byte comparison, not `localeCompare`: collation treats `-` as
  // ignorable punctuation, which is not the ordering these keys claim.
  const buckets = [...new Set([...materialized, ...options.serverBuckets])].sort((a, b) =>
    a < b ? -1 : a > b ? 1 : 0,
  );
  const incomplete = buckets.map((bucket) => {
    const span = bucketSpan(bucket, options.granularity);
    if (span === null) return true;
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return false;
    return !(span.startMs >= fromMs && span.endMs - 1 <= toMs);
  });
  return { buckets, incomplete };
}

/** Every bucket key from the one holding `fromMs` to the one holding `toMs`. */
function materializeBuckets(
  fromMs: number,
  toMs: number,
  granularity: TrendBucketGranularity,
): readonly string[] {
  const keys: string[] = [];
  let cursor = bucketStart(new Date(fromMs), granularity);
  // Bounded by the 90-day range cap: 90 day buckets at worst.
  while (cursor.getTime() <= toMs && keys.length < 400) {
    keys.push(bucketKey(cursor, granularity));
    cursor = nextBucketStart(cursor, granularity);
  }
  return keys;
}

/** UTC instant the bucket containing `date` starts at. */
function bucketStart(date: Date, granularity: TrendBucketGranularity): Date {
  if (granularity === 'month') {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  }
  const midnight = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  if (granularity === 'day') return new Date(midnight);
  // ISO weeks start on Monday; `getUTCDay()` puts Sunday at 0.
  const mondayOffset = (new Date(midnight).getUTCDay() + 6) % 7;
  return new Date(midnight - mondayOffset * MS_PER_DAY);
}

function nextBucketStart(start: Date, granularity: TrendBucketGranularity): Date {
  if (granularity === 'month') {
    return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
  }
  return new Date(start.getTime() + (granularity === 'week' ? 7 : 1) * MS_PER_DAY);
}

/**
 * Bucket key for a bucket START, byte-identical to what Postgres
 * `TO_CHAR(..., 'YYYY-MM-DD' | 'IYYY-"W"IW' | 'YYYY-MM')` emits.
 */
function bucketKey(start: Date, granularity: TrendBucketGranularity): string {
  const iso = start.toISOString();
  if (granularity === 'day') return iso.slice(0, 10);
  if (granularity === 'month') return iso.slice(0, 7);
  // ISO-8601 week: the week-numbering YEAR is the year of that week's
  // Thursday, and week 1 is the week holding January 4th.
  const thursday = new Date(start.getTime() + 3 * MS_PER_DAY);
  const isoYear = thursday.getUTCFullYear();
  const week1Monday = bucketStart(new Date(Date.UTC(isoYear, 0, 4)), 'week');
  const week = Math.round((start.getTime() - week1Monday.getTime()) / (7 * MS_PER_DAY)) + 1;
  return `${String(isoYear).padStart(4, '0')}-W${pad2(week)}`;
}

/** Half-open `[startMs, endMs)` a bucket KEY stands for; null if unparsable. */
function bucketSpan(
  key: string,
  granularity: TrendBucketGranularity,
): { readonly startMs: number; readonly endMs: number } | null {
  const start = bucketStartFromKey(key, granularity);
  if (start === null) return null;
  return { startMs: start.getTime(), endMs: nextBucketStart(start, granularity).getTime() };
}

function bucketStartFromKey(key: string, granularity: TrendBucketGranularity): Date | null {
  if (granularity === 'day') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return null;
    return new Date(`${key}T00:00:00.000Z`);
  }
  if (granularity === 'month') {
    if (!/^\d{4}-\d{2}$/.test(key)) return null;
    return new Date(`${key}-01T00:00:00.000Z`);
  }
  if (!/^\d{4}-W\d{2}$/.test(key)) return null;
  const isoYear = Number.parseInt(key.slice(0, 4), 10);
  const week = Number.parseInt(key.slice(6), 10);
  const week1Monday = bucketStart(new Date(Date.UTC(isoYear, 0, 4)), 'week');
  return new Date(week1Monday.getTime() + (week - 1) * 7 * MS_PER_DAY);
}

/**
 * Input that neither hit nor filled the cache:
 * `max(0, cacheEligibleInputTokens − cacheReadTokens − cacheWriteTokens)`.
 *
 * `cacheEligibleInputTokens` is the whole prompt and the only backend
 * total that means the same thing on every lane. Anthropic and Gemini
 * add the cache segments into it; OpenAI-family stored `inputTokens`
 * already contains cache reads, so a card that prints `inputTokens`
 * beside the cache-read tile counts those tokens twice. The model mix
 * and the trend already render this remainder (`uncachedInputTokens`).
 *
 * Clamped at 0. A negative remainder is a lane that reported more cache
 * than eligible input, not a token count.
 */
export function uncachedInput(parts: {
  readonly cacheEligibleInputTokens: number;
  readonly cacheReadTokens: number;
  readonly cacheWriteTokens: number;
}): number {
  return Math.max(
    0,
    parts.cacheEligibleInputTokens - parts.cacheReadTokens - parts.cacheWriteTokens,
  );
}

/**
 * Token-count display: exact grouping below 10k, compact (`324.6M`)
 * above — large counts stay scannable in KPI cards and table cells.
 * Negative and non-finite counts render as the `--` placeholder rather
 * than leaking `NaN` into the page.
 */
export function formatCompactCount(count: number): string {
  if (!Number.isFinite(count) || count < 0) return '--';
  if (count < 10000) return formatTokens(count);
  return COMPACT_FORMATTER.format(Math.floor(count));
}

/**
 * `152 秒 · 3 张`. The same unit phrase the overview activity cell
 * prints, so a second and an image read the same on both pages.
 * `unitLabel` is the translated noun (`requests.unit_billing.unit.*`).
 * Empty input is `''`: the overview drops the phrase, the usage card
 * substitutes its own zero.
 */
export function formatUnitQuantities(
  units: readonly UsageHourlyUnitQuantity[] | undefined,
  unitLabel: (unit: UsageHourlyUnitQuantity['unit']) => string,
): string {
  return (units ?? [])
    .map((entry) => `${formatTokens(entry.quantity)} ${unitLabel(entry.unit)}`)
    .join(' · ');
}

/** A 0..1 share as a percent, one decimal; non-finite renders as `--`. */
export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return '--';
  return PERCENT_FORMATTER.format(value);
}

/** `formatPercent` for API fields that are null when undefined-by-data. */
export function formatNullablePercent(value: number | null): string {
  return value === null ? '--' : formatPercent(value);
}

/** Multiplier display (`15.0x`) for read/write style ratios. */
export function formatRatio(value: number | null): string {
  return value === null || !Number.isFinite(value) ? '--' : `${value.toFixed(1)}x`;
}

/** Ratio that is null — not zero — when the denominator carries no data. */
export function nullableRatio(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null;
}

/** Latency in milliseconds with the unit attached; null renders as `--`. */
export function formatLatencyMs(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '--';
  return `${formatTokens(value)} ms`;
}

/** Local-time `YYYY-MM-DD` for window boundaries. */
export function formatIsoDate(iso: string): string {
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) return iso.length > 0 ? iso : '--';
  const d = new Date(ms);
  return [d.getFullYear(), pad2(d.getMonth() + 1), pad2(d.getDate())].join('-');
}

/** Local-time `HH:mm` for "refreshed at" style stamps. */
export function formatShortTime(iso: string): string {
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) return '--';
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Kebab slug for `data-testid` suffixes built from server-supplied ids
 * and model names, so a selector never has to quote arbitrary
 * punctuation.
 */
export function testIdSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
